import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { DataTableProps, DataRow } from "./dataTable.interface";
import DataTableHead from "./DataTableHead";
import useTable from "./useTable";
import Skeleton from "@mui/material/Skeleton";
import { CSSProperties, useEffect, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

export default function DataTable({
  data,
  rowsPerPage,
  order: initialOrder,
  orderBy: initialOrderBy,
  page: initialPage,
  headCells,
  minWidth = 250,
  isLoading,
  total,
  messageEmptyData = "No data found",
  showPagination = true,
  onChangePage,
  onChangeSize,
  onChangeSort,
  rowSx,
  onRowClick,
  maxHeight,
  headCellFontSize = "13px",
  emptyDataFontSize = "13px",
  paginationFontSize = "12px",
  bodyCellFontSize,
  visibleColumns,
}: DataTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const {
    order,
    orderBy,
    selected,
    page,
    handleRequestSort,
    handleSelectAllClick,
    handleClick,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTable(data, initialOrder, initialOrderBy, initialPage);

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  // Filter visible columns based on responsive settings and visibleColumns prop
  const filteredHeadCells = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return headCells.filter((cell) => visibleColumns.includes(cell.id));
    }

    return headCells.filter((cell) => {
      // Always show if explicitly marked as always visible
      if (cell.alwaysVisible) return true;

      // Hide on mobile if specified
      if (isMobile && cell.hideOnMobile) return false;

      // Hide on tablet if specified
      if (isTablet && cell.hideOnTablet) return false;

      return true;
    });
  }, [headCells, visibleColumns, isMobile, isTablet]);

  useEffect(() => {
    if (order !== initialOrder || orderBy !== initialOrderBy) {
      onChangeSort?.(order, orderBy);
    }
  }, [order, orderBy, onChangeSort]);

  return (
    <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      <TableContainer
        sx={{
          width: "100%",
          maxWidth: "100%",
          "@media (max-width: 600px)": {
            maxWidth: "calc(100vw - 96px)",
          },
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: maxHeight,
          "&::-webkit-scrollbar": {
            height: "8px",
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
      >
        <Table
          stickyHeader
          sx={{ minWidth: minWidth, width: "100%" }}
          aria-labelledby="tableTitle"
        >
          <DataTableHead
            numSelected={selected.length}
            order={order}
            orderBy={String(orderBy)}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
            headCells={filteredHeadCells}
            headCellFontSize={headCellFontSize}
          />
          <TableBody>
            {isLoading ? (
              <TableRow hover role="row" tabIndex={-1} key="skeleton-row">
                {filteredHeadCells.map((headCell, index) => (
                  <TableCell key={`skeleton-table-cell-${index}`}>
                    <Skeleton
                      data-testid="skeleton"
                      variant="text"
                      width={100}
                      height={32}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ) : (
              <>
                {data.map((row: DataRow, index: number) => {
                  const isItemSelected = isSelected(+row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => {
                        handleClick(event, +row.id);
                        if (onRowClick) {
                          onRowClick(event, row.id);
                        }
                      }}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={`table-row-${row.id}-${index}`}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: (row.style as CSSProperties)
                          ?.backgroundColor,
                        "&:hover": {
                          backgroundColor: "#F4F4F9",
                        },
                        ...(rowSx ? rowSx(row) : {}),
                      }}
                    >
                      {filteredHeadCells.map((headCell, i) => {
                        const key = headCell.id as string;
                        const skipKeys = ["id", "style", "cellStyles"];
                        if (!skipKeys.includes(key)) {
                          let cellValue = row[key as keyof DataRow];
                          if (
                            cellValue &&
                            typeof cellValue === "object" &&
                            "node" in cellValue
                          ) {
                            cellValue = cellValue.node;
                          }
                          return (
                            <TableCell
                              key={`cell-${key}-${i}`}
                              component={i === 0 ? "th" : "td"}
                              id={i === 0 ? labelId : undefined}
                              scope={i === 0 ? "row" : undefined}
                              padding="normal"
                              align={
                                headCell?.id === "action"
                                  ? "right"
                                  : headCell?.align
                              }
                              sx={{
                                fontSize: bodyCellFontSize,
                                ...(row.cellStyles?.[key] || {}),
                                ...(headCell?.cellStyles || {}),
                              }}
                            >
                              {cellValue as React.ReactNode}
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  );
                })}
              </>
            )}
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={filteredHeadCells.length}
                  sx={{
                    textAlign: "center",
                    padding: "1rem",
                    fontSize: emptyDataFontSize,
                  }}
                >
                  {messageEmptyData}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total || data.length}
          rowsPerPage={rowsPerPage}
          page={initialPage || page}
          onPageChange={onChangePage || handleChangePage}
          onRowsPerPageChange={onChangeSize || handleChangeRowsPerPage}
          disabled={isLoading}
          slotProps={{
            select: {
              sx: {
                fontSize: paginationFontSize,
              },
              MenuProps: {
                MenuListProps: {
                  sx: {
                    fontSize: paginationFontSize,
                    "& .MuiMenuItem-root": {
                      fontSize: `${paginationFontSize} !important`,
                    },
                    "& .MuiButtonBase-root": {
                      fontSize: `${paginationFontSize} !important`,
                    },
                  },
                },
              },
            },
          }}
          sx={{
            "& .MuiTablePagination-selectLabel": {
              fontSize: paginationFontSize,
            },
            "& .MuiTablePagination-displayedRows": {
              fontSize: paginationFontSize,
            },
            "& .MuiTablePagination-select": {
              fontSize: `${paginationFontSize} !important`,
            },
            "& .MuiSelect-select": { fontSize: paginationFontSize },
            "& .MuiTablePagination-menuItem": {
              fontSize: `${paginationFontSize} !important`,
            },
            "& .MuiButtonBase-root.MuiMenuItem-root.MuiTablePagination-menuItem":
              {
                fontSize: `${paginationFontSize} !important`,
              },
            "& .MuiInputBase-root": { fontSize: paginationFontSize },
          }}
        />
      )}
    </Box>
  );
}
